import IProject from '../model/IProject'

// One folder in the breadcrumb trail. The tenant (site) crumb is not part of
// the trail; it is always rendered in front of it.
export interface ICrumb {
  id: string
  name: string
}

// Navigations carry the trail of the folder being opened in router state, so
// the breadcrumbs render immediately instead of waiting on the /path endpoint.
export interface IBreadcrumbLocationState {
  breadcrumbTrail?: ICrumb[]
}

export const trailState = (trail: ICrumb[]): IBreadcrumbLocationState => ({
  breadcrumbTrail: trail
})

interface IResolveTrailArgs {
  routeId?: string
  tenantId: string | null
  locationState: unknown
  path: IProject[] | null
  pathProjectId: string | null
  detailsName?: string
  // The last complete trail seen for this folder in this session. Router state
  // is lost when a search-param update replaces the history entry, so this
  // keeps the trail rather than dropping back to a partial one.
  remembered?: ICrumb[]
}

export interface IResolvedTrail {
  trail: ICrumb[]
  // Whether `trail` holds every ancestor of the current folder, i.e. whether
  // it is safe to extend for a child navigation.
  complete: boolean
  // Effective role per folder id, or null while /path is still loading for
  // the current folder.
  roles: Record<string, string> | null
}

// Router state for opening a child row of the current folder. An incomplete
// trail would show a broken breadcrumb, and rows left over from the previous
// folder while the next one loads are not children of the current one; in
// both cases the child resolves its own trail from /path instead.
export const childTrailState = (
  { trail, complete }: IResolvedTrail,
  rowsFolderId: string | null,
  routeId: string | undefined,
  child: ICrumb
): IBreadcrumbLocationState | undefined =>
  complete && rowsFolderId === routeId
    ? trailState([...trail, child])
    : undefined

const stateTrail = (locationState: unknown): ICrumb[] | null => {
  const trail = (locationState as IBreadcrumbLocationState | null)
    ?.breadcrumbTrail
  return Array.isArray(trail) ? trail : null
}

export const resolveTrail = ({
  routeId,
  tenantId,
  locationState,
  path,
  pathProjectId,
  detailsName,
  remembered
}: IResolveTrailArgs): IResolvedTrail => {
  if (!routeId) return { trail: [], complete: true, roles: {} }

  const pathLoaded = pathProjectId === routeId

  const roles = pathLoaded
    ? Object.fromEntries(
        (path || [])
          .filter(p => p.effectiveUserRole)
          .map(p => [p.id, p.effectiveUserRole as string])
      )
    : null

  // The endpoint is authoritative (it also picks up renames), so once it has
  // answered for this folder it wins over whatever the navigation carried.
  if (pathLoaded && path) {
    const trail = path
      .filter(p => p.id !== tenantId)
      .map(({ id, name }) => ({ id, name }))
    return { trail, complete: true, roles }
  }

  const carried = stateTrail(locationState)
  if (carried && carried[carried.length - 1]?.id === routeId) {
    return { trail: carried, complete: true, roles }
  }

  if (remembered) return { trail: remembered, complete: true, roles }

  if (routeId === tenantId) return { trail: [], complete: true, roles }

  // Hard load (refresh / deep link): only the current folder is known until
  // /path answers with its ancestors.
  return {
    trail: detailsName ? [{ id: routeId, name: detailsName }] : [],
    complete: false,
    roles
  }
}
